<template>
  <div class="credential-settings-shell">
    <div class="credential-settings-toolbar">
      <div>
        <span class="credential-settings-eyebrow">Encrypted Integration Credentials</span>
        <h5>LifeWORTH Enrollee Sync</h5>
        <p>Manage the protected remote credentials used when a super admin manually syncs the LifeWORTH enrollee list.</p>
      </div>
      <span class="credential-status" :class="{ 'is-enabled': form.enabled }">
        {{ form.enabled ? "Enabled" : "Disabled" }}
      </span>
    </div>

    <div class="credential-message is-error" v-if="error">{{ error }}</div>
    <div class="credential-message is-success" v-if="success">{{ success }}</div>

    <LoadingDiv v-if="isLoading" />

    <form v-else class="credential-form" @submit.prevent="handleSubmit">
      <label class="credential-toggle-row">
        <span>
          <strong>Enable LifeWORTH sync</strong>
          <em>When disabled, the sync button will fail safely and no remote enrollee pull can run.</em>
        </span>
        <input type="checkbox" class="filled-in" v-model="form.enabled" />
        <span></span>
      </label>

      <div class="credential-grid">
        <label>
          <span>Remote enrollee URL</span>
          <input v-model.trim="form.remote_enrollee_url" type="url" required autocomplete="off" />
        </label>

        <label>
          <span>HTTP method</span>
          <select v-model="form.method" class="browser-default" required>
            <option value="GET">GET</option>
            <option value="POST">POST</option>
          </select>
        </label>

        <label>
          <span>Timeout milliseconds</span>
          <input v-model.number="form.timeout_ms" type="number" min="1000" max="240000" required />
        </label>

      </div>

      <div class="credential-section-title">
        <strong>On-demand lookup</strong>
        <em>Used after a local enrollee search misses.</em>
      </div>

      <div class="credential-grid">
        <label>
          <span>Single enrollee URL</span>
          <input v-model.trim="form.single_enrollee_url" type="url" autocomplete="off" />
        </label>

        <label>
          <span>Single query key</span>
          <input v-model.trim="form.single_enrollee_query_key" type="text" autocomplete="off" />
        </label>

        <label>
          <span>Dependant URL</span>
          <input v-model.trim="form.dependant_url" type="url" autocomplete="off" />
        </label>

        <label>
          <span>Dependant query key</span>
          <input v-model.trim="form.dependant_query_key" type="text" autocomplete="off" />
        </label>

        <label>
          <span>Lookup timeout milliseconds</span>
          <input v-model.number="form.lookup_timeout_ms" type="number" min="1000" max="30000" />
        </label>

        <label>
          <span>Lookup scheme ID</span>
          <input v-model.number="form.lookup_scheme_id" type="number" min="1" />
        </label>
      </div>

      <label>
        <span>API key</span>
        <input
          v-model="form.api_key"
          type="password"
          :required="form.enabled && !hasApiKey"
          :placeholder="hasApiKey ? 'API key configured. Enter a new key to replace it.' : 'Enter LifeWORTH X-Api-Key'"
          autocomplete="new-password"
        />
      </label>

      <div class="credential-key-state">
        <i class="material-icons">{{ hasApiKey ? "lock" : "lock_open" }}</i>
        <span>{{ hasApiKey ? "API key is configured and encrypted on save." : "No API key has been saved yet." }}</span>
      </div>

      <div class="credential-actions">
        <button type="button" class="credential-secondary" :disabled="isSaving" @click="loadCredentials">
          <i class="material-icons">refresh</i>
          <span>Reload</span>
        </button>
        <button type="submit" class="credential-primary" :disabled="isSaving">
          <i class="material-icons">{{ isSaving ? "hourglass_empty" : "save" }}</i>
          <span>{{ isSaving ? "Saving" : "Save Credentials" }}</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { onMounted, reactive, ref } from "vue";
import getItems from "@/composables/getItems";
import putItem from "@/composables/putItem";
import LoadingDiv from "@/components/LoadingDiv.vue";

export default {
  name: "CredentialSetupComponent",
  components: { LoadingDiv },
  setup() {
    const isLoading = ref(true);
    const isSaving = ref(false);
    const error = ref(null);
    const success = ref(null);
    const hasApiKey = ref(false);
    const form = reactive({
      enabled: false,
      remote_enrollee_url: "",
      method: "GET",
      timeout_ms: 15000,
      month_query_key: "",
      single_enrollee_url: "",
      single_enrollee_query_key: "EmployeeNo",
      dependant_url: "",
      dependant_query_key: "IdEmployee",
      lookup_timeout_ms: 5000,
      lookup_scheme_id: null,
      api_key: "",
    });

    const fillForm = (data = {}) => {
      form.enabled = !!data.enabled;
      form.remote_enrollee_url = data.remote_enrollee_url || "";
      form.method = data.method || "GET";
      form.timeout_ms = data.timeout_ms || 15000;
      form.month_query_key = data.month_query_key || "";
      form.single_enrollee_url = data.single_enrollee_url || "";
      form.single_enrollee_query_key = data.single_enrollee_query_key || "EmployeeNo";
      form.dependant_url = data.dependant_url || "";
      form.dependant_query_key = data.dependant_query_key || "IdEmployee";
      form.lookup_timeout_ms = data.lookup_timeout_ms || 5000;
      form.lookup_scheme_id = data.lookup_scheme_id || null;
      form.api_key = "";
      hasApiKey.value = !!data.has_api_key;
    };

    const loadCredentials = async () => {
      isLoading.value = true;
      error.value = null;
      success.value = null;

      const { res, err } = await getItems("custom/lifeworth/credentials/enrollee-sync");

      if (err.value) {
        error.value = err.value;
      } else {
        fillForm(res.value?.data);
      }

      isLoading.value = false;
    };

    const handleSubmit = async () => {
      isSaving.value = true;
      error.value = null;
      success.value = null;

      const payload = {
        enabled: form.enabled,
        remote_enrollee_url: form.remote_enrollee_url,
        method: form.method,
        timeout_ms: form.timeout_ms,
        month_query_key: form.month_query_key || null,
        single_enrollee_url: form.single_enrollee_url || null,
        single_enrollee_query_key: form.single_enrollee_query_key || "EmployeeNo",
        dependant_url: form.dependant_url || null,
        dependant_query_key: form.dependant_query_key || "IdEmployee",
        lookup_timeout_ms: form.lookup_timeout_ms || 5000,
        lookup_scheme_id: form.lookup_scheme_id || null,
      };

      if (form.api_key) {
        payload.api_key = form.api_key;
      }

      const { res, err } = await putItem("custom/lifeworth/credentials", "enrollee-sync", payload);

      if (err.value) {
        error.value = err.value;
      } else {
        fillForm(res.value?.data);
        success.value = res.value?.message || "LifeWORTH credentials saved.";
      }

      isSaving.value = false;
    };

    onMounted(loadCredentials);

    return {
      form,
      hasApiKey,
      isLoading,
      isSaving,
      error,
      success,
      loadCredentials,
      handleSubmit,
    };
  },
};
</script>

<style scoped>
.credential-settings-shell {
  color: #263238;
}

.credential-settings-toolbar,
.credential-form {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: none;
  padding: 20px;
}

.credential-settings-toolbar {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 14px;
}

.credential-settings-eyebrow {
  color: #00897b;
  display: block;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
}

.credential-settings-toolbar h5 {
  color: #263238;
  font-size: 1.2rem;
  font-weight: 800;
  margin: 5px 0;
}

.credential-settings-toolbar p,
.credential-toggle-row em,
.credential-key-state {
  color: #607d8b;
  font-size: 0.88rem;
  line-height: 1.45;
  margin: 0;
}

.credential-status {
  background: #fff8e1;
  border: 1px solid #f0cf6a;
  border-radius: 999px;
  color: #7a5200;
  flex: 0 0 auto;
  font-size: 0.78rem;
  font-weight: 800;
  padding: 7px 11px;
}

.credential-status.is-enabled {
  background: #e0f2f1;
  border-color: #80cbc4;
  color: #00695c;
}

.credential-form {
  display: grid;
  gap: 16px;
}

.credential-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(0, 1.5fr) minmax(150px, 0.5fr);
}

.credential-form label,
.credential-toggle-row {
  display: grid;
  gap: 7px;
}

.credential-form label > span,
.credential-toggle-row strong {
  color: #455a64;
  font-size: 0.74rem;
  font-weight: 800;
  text-transform: uppercase;
}

.credential-section-title {
  border-top: 1px solid #dfe7ea;
  display: grid;
  gap: 3px;
  padding-top: 4px;
}

.credential-section-title strong {
  color: #263238;
  font-size: 0.88rem;
  font-weight: 800;
}

.credential-section-title em {
  color: #607d8b;
  font-size: 0.82rem;
  font-style: normal;
}

.credential-form input,
.credential-form select {
  border: 1px solid #c7d3d8 !important;
  border-radius: 6px !important;
  box-sizing: border-box !important;
  color: #263238;
  height: 44px !important;
  margin: 0 !important;
  padding: 0 12px !important;
}

.credential-toggle-row {
  align-items: center;
  border: 1px solid #dfe7ea;
  border-radius: 6px;
  grid-template-columns: 1fr auto;
  padding: 14px;
}

.credential-toggle-row em {
  display: block;
  font-style: normal;
  margin-top: 3px;
  text-transform: none;
}

.credential-key-state {
  align-items: center;
  background: #f8fafb;
  border: 1px solid #dfe7ea;
  border-radius: 6px;
  display: flex;
  gap: 9px;
  padding: 10px 12px;
}

.credential-key-state i {
  color: #00897b;
  font-size: 20px;
}

.credential-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.credential-primary,
.credential-secondary {
  align-items: center;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  font-weight: 800;
  gap: 7px;
  min-height: 40px;
  padding: 0 14px;
}

.credential-primary {
  background: #00897b;
  color: #ffffff;
}

.credential-secondary {
  background: #ffffff;
  border-color: #b7c8cf;
  color: #263238;
}

.credential-primary:disabled,
.credential-secondary:disabled {
  cursor: default;
  opacity: 0.7;
}

.credential-message {
  border-radius: 6px;
  font-weight: 700;
  margin-bottom: 14px;
  padding: 12px 14px;
}

.credential-message.is-error {
  background: #fff8e1;
  border: 1px solid #f0cf6a;
  color: #7a5200;
}

.credential-message.is-success {
  background: #e0f2f1;
  border: 1px solid #80cbc4;
  color: #00695c;
}

@media only screen and (max-width: 700px) {
  .credential-settings-toolbar,
  .credential-actions {
    flex-direction: column;
  }

  .credential-grid {
    grid-template-columns: 1fr;
  }

  .credential-primary,
  .credential-secondary {
    justify-content: center;
    width: 100%;
  }
}
</style>
